/**
 * Permission Management Page
 * Requirements: 15.1-15.9 - Permission management
 */
import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { History as HistoryIcon, Edit as EditIcon } from '@mui/icons-material';
import { permissionApi } from '../api/permissionApi';
import { ExecutiveSetupPanel, type ExecutiveTarget } from '../components/ExecutiveSetupPanel';
import {
  PermissionLevel,
  type EmployeesByPermissionResponse,
  type PermissionHistoryResponse,
} from '../types/permission.types';

export const PermissionManagementPage = () => {
  const [selectedLevel, setSelectedLevel] = useState<PermissionLevel>(PermissionLevel.SELF_ONLY);
  const [employeeData, setEmployeeData] = useState<EmployeesByPermissionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [newPermissionLevel, setNewPermissionLevel] = useState<PermissionLevel>(
    PermissionLevel.SELF_ONLY
  );

  // 社長設定パネルの対象。行の「社長設定」を押すと表に下に開く。
  const [execTarget, setExecTarget] = useState<ExecutiveTarget | null>(null);
  const execPanelRef = useRef<HTMLDivElement>(null);

  // 一覧が長いとパネルが画面外に出て「押しても何も起きない」ように見えるため、
  // 開いたら必ずその位置まで送る。
  useEffect(() => {
    if (execTarget) {
      execPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [execTarget]);

  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyData, setHistoryData] = useState<PermissionHistoryResponse | null>(null);

  const loadEmployeesByPermission = async (level: PermissionLevel) => {
    try {
      setLoading(true);
      setError(null);
      const data = await permissionApi.getEmployeesByPermission(level);
      setEmployeeData(data);
    } catch (err) {
      setError('権限レベル別の従業員一覧取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleLevelChange = (level: PermissionLevel) => {
    setSelectedLevel(level);
    loadEmployeesByPermission(level);
  };

  const handleOpenEditDialog = (employeeId: string, currentLevel: PermissionLevel) => {
    setSelectedEmployeeId(employeeId);
    setNewPermissionLevel(currentLevel);
    setEditDialogOpen(true);
  };

  const handleUpdatePermission = async () => {
    try {
      await permissionApi.setPermissionLevel(selectedEmployeeId, {
        permissionLevel: newPermissionLevel,
      });
      setSuccess('権限レベルを更新しました');
      setEditDialogOpen(false);
      loadEmployeesByPermission(selectedLevel);
    } catch (err) {
      setError('権限レベルの更新に失敗しました');
    }
  };

  const handleOpenHistory = async (employeeId: string) => {
    try {
      const data = await permissionApi.getPermissionHistory(employeeId);
      setHistoryData(data);
      setHistoryDialogOpen(true);
    } catch (err) {
      setError('権限変更履歴の取得に失敗しました');
    }
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        権限管理
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box mb={3}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>権限レベルで絞り込み</InputLabel>
          <Select
            value={selectedLevel}
            onChange={(e) => handleLevelChange(e.target.value as PermissionLevel)}
            label="権限レベルで絞り込み"
          >
            {Object.values(PermissionLevel).map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={() => loadEmployeesByPermission(selectedLevel)}
          sx={{ ml: 2 }}
        >
          検索
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : employeeData ? (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>
              {employeeData.permissionLevel} - {employeeData.count}名
            </Typography>

            {/* 選択した社員の社長設定（DB権限 → Cognito反映 → 会社のGB組織 をまとめて実行）。
                一覧の下ではなく上に置く。一覧は行数ぶん伸びるため、下に置くと画面外になる。 */}
            <div ref={execPanelRef}>
              {execTarget && (
                <ExecutiveSetupPanel
                  target={execTarget}
                  onDone={() => void handleLevelChange(selectedLevel)}
                />
              )}
            </div>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>名前</TableCell>
                    <TableCell>メールアドレス</TableCell>
                    <TableCell>会社</TableCell>
                    <TableCell>部門</TableCell>
                    <TableCell>権限レベル</TableCell>
                    <TableCell align="right">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employeeData.employees.map((emp) => (
                    <TableRow key={emp.id} selected={execTarget?.id === emp.id}>
                      <TableCell>{emp.name}</TableCell>
                      <TableCell>{emp.email}</TableCell>
                      <TableCell>{emp.companyName || '-'}</TableCell>
                      <TableCell>
                        {emp.departments && emp.departments.length > 0 ? (
                          emp.departments.map((d) => (
                            <Chip
                              key={d.id}
                              label={d.name}
                              size="small"
                              color={d.isPrimary ? 'primary' : 'default'}
                              sx={{ mr: 0.5 }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{emp.permissionLevel}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant={execTarget?.id === emp.id ? 'contained' : 'outlined'}
                          onClick={() =>
                            setExecTarget(
                              execTarget?.id === emp.id
                                ? null
                                : {
                                    id: emp.id,
                                    name: emp.name,
                                    email: emp.email,
                                    companyId: emp.companyId,
                                    companyName: emp.companyName,
                                    permissionLevel: emp.permissionLevel,
                                  }
                            )
                          }
                          sx={{ mr: 1 }}
                        >
                          社長設定
                        </Button>
                        <IconButton
                          onClick={() => handleOpenEditDialog(emp.id, emp.permissionLevel)}
                          size="small"
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleOpenHistory(emp.id)} size="small">
                          <HistoryIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : null}

      {/* Edit Permission Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>権限レベルの変更</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>新しい権限レベル</InputLabel>
            <Select
              value={newPermissionLevel}
              onChange={(e) => setNewPermissionLevel(e.target.value as PermissionLevel)}
              label="新しい権限レベル"
            >
              {Object.values(PermissionLevel).map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleUpdatePermission} variant="contained">
            更新
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permission History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>権限変更履歴</DialogTitle>
        <DialogContent>
          {historyData && (
            <List>
              {historyData.history.map((item) => (
                <ListItem key={item.id}>
                  <ListItemText
                    primary={`${item.oldValue || '(なし)'} → ${item.newValue}`}
                    secondary={`${new Date(item.timestamp).toLocaleString()} - 変更者: ${item.changedBy?.name || '不明'}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
